import type { Translator } from "@/lib/translation-context";
import { OFFLINE_PAYMENT_ERROR_CODES } from "@tripcart/shared";

/**
 * What each refusal means, in words a traveler uses.
 *
 * Keyed on the API's `error.code`, which is stable and meant for machines.
 * The `message` beside it is written by and for engineers — the domain layer
 * really does answer a failed booking with "Concurrent modification detected;
 * retry" and "Requested 4 seats but only 2 available". Passing those straight
 * through is specific, and useless: a customer cannot act on either.
 *
 * So the rule is: be specific about WHICH thing went wrong (the code), and say
 * it the way a person would. Where a refusal has no entry here we fall back to
 * the server's own message, which for the domain errors that matter is usually
 * readable, and to the caller's wording when it is not.
 *
 * Copy rule (see `/CLAUDE.md`): state facts. Where money is involved, say
 * whether it was taken — that is the traveler's first question and the one
 * thing a generic message never answers.
 */
export function humanForCode(
  code: string | undefined,
  t: Translator,
): string | undefined {
  if (!code) return undefined;
  switch (code) {
    // — availability ————————————————————————————————————————————————
    case "CAPACITY_EXHAUSTED":
      return t(
        "There aren't enough places left on this departure for the number of travelers you chose.",
      );
    case "MAX_PAX_VIOLATION":
      return t("That is more travelers than this tour takes in one booking.");
    case "INVALID_DEPARTURE_DATE":
      return t("That departure date is no longer available.");
    case "EXPERIENCE_NOT_FOUND":
    case "PACKAGE_NOT_FOUND":
      return t("This tour is no longer available to book.");

    // — the cart went stale while they were filling the form ————————————
    case "QUOTE_EXPIRED":
    case "QUOTE_NOT_FOUND":
    case "QUOTE_NOT_OPEN":
      return t(
        "Your prices are out of date. Go back to your cart to refresh them, then book again.",
      );
    case "PRICING_CHANGED":
      return t(
        "The price changed while you were booking. Go back to your cart to see the new total.",
      );
    case "OPTIMISTIC_LOCK_LOST":
    case "BOOKING_VERSION_CONFLICT":
      return t(
        "This booking changed while you were working on it. Go back to your cart and start again.",
      );
    case "CHECKOUT_FORM_OUT_OF_DATE":
      return t(
        "The booking questions changed while you were filling them in. Reload the page to see the current ones.",
      );
    case "IDEMPOTENCY_KEY_REUSED":
      return t(
        "This booking has already been submitted. Check your email for the confirmation before trying again.",
      );

    // — what they entered —————————————————————————————————————————————
    //
    // CHECKOUT_FIELD_ANSWER_MISSING and CHECKOUT_FIELD_ANSWER_INVALID are
    // deliberately NOT mapped. Their server messages already name the field —
    // "Passport Scan is required", "Date of birth: not a real calendar date"
    // (`booking.errors.ts`) — and this map returns BEFORE the message is read,
    // so an entry here would replace the one thing the traveler needs with
    // "one of your answers was not accepted". That is the vagueness this whole
    // helper exists to remove. A code belongs in this map only when its own
    // message is worse than the sentence replacing it.
    case "INVALID_PICKUP":
      return t("The pickup point you chose is no longer available.");
    case "INVALID_ADDON":
      return t("One of the extras you chose is no longer available.");
    case "COUPON_NO_LONGER_VALID":
      return t("That promo code is no longer valid.");
    case "MIXED_CURRENCY":
      return t(
        "Your cart holds tours priced in different currencies, which cannot be paid for together.",
      );

    // — payment ———————————————————————————————————————————————————————
    // Each says whether money moved, because that is what they will ask.
    case "PAYMENT_PROVIDER_NOT_CONFIGURED":
      return t(
        "Online payment is not available for this booking. Your booking is held and nothing has been charged — contact us to arrange payment.",
      );
    case "PAYMENT_GATEWAY_REQUEST_FAILED":
      return t(
        "We could not reach the payment provider. Nothing has been charged.",
      );
    case "BOOKING_NOT_PAYABLE":
      return t("This booking cannot be paid for at the moment.");
    case "DEPOSIT_NOT_OFFERED":
      return t("A deposit is not offered for this booking.");
    case OFFLINE_PAYMENT_ERROR_CODES.methodUnavailable:
      return t("This payment option is no longer available. Choose another.");
    case OFFLINE_PAYMENT_ERROR_CODES.depositNotAllowed:
      return t("This payment method cannot be combined with a deposit.");
    case OFFLINE_PAYMENT_ERROR_CODES.unpaidLimitReached:
      return t(
        "You already have an unpaid booking for this date. Pay for it first, or choose Pay online.",
      );
    case OFFLINE_PAYMENT_ERROR_CODES.paymentInFlight:
      return t(
        "A payment for this booking is already in progress. Finish it, or wait a moment and try again.",
      );
    case OFFLINE_PAYMENT_ERROR_CODES.switchRefused:
      return t(
        "This booking can no longer be changed to this payment method. Go back and start your booking again.",
      );
    case OFFLINE_PAYMENT_ERROR_CODES.transferAlreadyReported:
      return t(
        "You have already told us about this transfer. It cannot be sent again or changed.",
      );
    case OFFLINE_PAYMENT_ERROR_CODES.transferDateInFuture:
      return t("The date you sent the transfer cannot be in the future.");
    case OFFLINE_PAYMENT_ERROR_CODES.transferActionRefused:
      return t(
        "This booking is not waiting for a bank transfer, so this cannot be done.",
      );
    case OFFLINE_PAYMENT_ERROR_CODES.noSpotsLeft:
      return t("Sorry, this date is now full. Contact the operator.");

    // — the whole site —————————————————————————————————————————————————
    case "TENANT_SUSPENDED":
    case "NO_PLAN":
      return t("This site is not taking bookings at the moment.");

    // — our own plumbing ——————————————————————————————————————————————
    // These are the codes traveler-web's own BFF routes emit when the API
    // does not answer usably. Their `message` is assembled from whatever
    // replied — a proxy error page, a platform interstitial — so it must
    // never be the thing shown. Matching the CODE returns before the message
    // is ever consulted, which is an allowlist rather than a guess at the
    // shape of the next outage. The routes no longer put the body on the
    // wire either; this is the second lock on the same door.
    case "UPSTREAM_UNREACHABLE":
    case "UPSTREAM_NON_JSON":
    case "UPSTREAM_EMPTY":
    case "UPSTREAM_TIMEOUT":
      return t(
        "We could not reach the booking service. Nothing has been charged.",
      );

    // — the database layer ————————————————————————————————————————————
    // `postgres-error-mapper.ts` writes for the operator dashboard, where
    // "Remove the duplicate before saving" makes sense. On a storefront it is
    // advice about a form the customer is not looking at, and
    // MISSING_REQUIRED_FIELD interpolates a database COLUMN NAME.
    case "DB_ERROR":
    case "DB_CONCURRENT_UPDATE":
    case "DUPLICATE_RESOURCE":
    case "MISSING_REQUIRED_FIELD":
    case "FOREIGN_KEY_MISSING":
    case "CHECK_VIOLATION":
      return t("We could not save that. Nothing has been changed.");

    default:
      return undefined;
  }
}

/**
 * Field-level messages that are written for whoever wrote the schema, not for
 * the person filling the form. "Invalid UUID" is about an id the traveler has
 * never seen and cannot change; showing it is worse than showing nothing,
 * because they will try to fix it.
 *
 * Anything matching this is dropped, and the refusal falls back to the
 * code's own sentence above.
 */
// `received \w+` used to be in here and had to go: it swallowed "We have not
// received payment for this booking." — ordinary, high-frequency copy on a
// booking storefront. A swallowed reason falls back to the caller's generic
// wording, which is the very failure this file exists to prevent, so an
// over-broad rule here costs as much as a missing one. Zod's real phrasing is
// "expected X, received Y", already covered by the alternative before it.
// `object`, `array`, `enum` and `nan` went for the same reason ("This tour
// object is sold out."); what remains does not double as travel English.
// `too small: expected` / `too big: expected` are Zod v4's size-bound wording
// ("Too small: expected array to have >=1 items"). They carry "expected" but
// never "received", so the alternative above does not reach them, and dropping
// the bare `object`/`array` words left nothing that did.
const MACHINE_PHRASING =
  /\b(uuid|iso ?8601|expected .+ received|too (?:small|big): expected|nullable|undefined|typeof|discriminat|int32|bigint|string literal)\b/i;

/**
 * Things that must never reach a customer's screen, whatever else is true of
 * the message. This page is public and unauthenticated, so anything here is a
 * disclosure, not just bad copy.
 *
 * Each was probed against the filter above and got through it, which is why
 * they are listed separately rather than folded into the phrasing regex:
 *
 *   00000000-0000-4000-8000-000000000002              (a real API `message`)
 *   /Users/…/apps/traveler-api/dist/main.js           (a path from a throw)
 *   duplicate key value violates unique constraint …  (Postgres, verbatim)
 *   connect ECONNREFUSED 127.0.0.1:5432               (internal host and port)
 *
 * The last rule is the catch-all: a single long token with no spaces is an
 * identifier of some kind — an id, a slug, a hash — and never a sentence.
 */
const DISCLOSURE = [
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i, // uuid
  // A FILESYSTEM path, anchored to a real root. Matching any slash swallowed
  // ordinary copy — "Go back to /my-bookings" was blocked while "/cart" got
  // through, so whether an in-app link survived depended on its length. That
  // is unpredictable for anyone writing copy.
  /(^|\s)(\/(users|home|var|opt|app|tmp|etc|srv)\/|[a-z]:\\)/i,
  // Postgres, by markers that do not double as English. `relation` and
  // `column` are gone: "What is your relation to the lead traveller?" is a
  // plausible operator-authored question, and swallowing it produces exactly
  // the vague message this whole helper exists to avoid.
  // `does not exist` is pinned to the QUOTED-IDENTIFIER shape Postgres emits
  // (`relation "bookings" does not exist`). As a bare two-word phrase it also
  // swallowed "Your booking reference does not exist." and "That promo code
  // does not exist." — plausible copy, and the third time this list has had to
  // retreat from an English phrase that reads as a database term.
  /\b(pg_[a-z]+|duplicate key|violates \w+ constraint|syntax error at|unique constraint)\b/i,
  /\b(relation|column|table|type|function|schema|constraint|sequence) "[^"]+" does not exist/i,
  // Node/libuv transport codes, named explicitly. The shape-based rule this
  // replaces (`/\bE[A-Z]{4,}\b/`) was wrong in both directions: it swallowed
  // any 5+ letter capitalised word starting with E — EMAIL, EMERGENCY, ENTRY,
  // EVEREST, a promo code like EARLYBIRD — which is exactly how an operator
  // writes a checkout question label, and those labels are the reason
  // CHECKOUT_FIELD_ANSWER_MISSING is deliberately left unmapped in the code
  // table above. It also still missed EAI_AGAIN, the one code it was widened
  // to catch, because only two letters precede the underscore.
  /\b(E(?:CONN(?:REFUSED|RESET|ABORTED)|TIMEDOUT|NOTFOUND|HOSTUNREACH|NETUNREACH|NETDOWN|ADDRINUSE|ADDRNOTAVAIL|ACCES|NOENT|MFILE|NFILE|PIPE|PROTO|AI_[A-Z]+)|ERR_[A-Z_]+)\b/,
  /\bsocket hang up\b/i,
  /\b(node_modules|at [A-Z]\w+\.|\.js:\d+|stack trace)\b/i, // stack leakage
  // Next's own text for a Server Action that threw in a production build:
  // "An error occurred in the Server Components render… The specific message
  // is omitted in production builds… A digest property is included…". It
  // reads as prose, so nothing else here catches it, and the cart would print
  // that paragraph as the reason a seat count did not change.
  /\b(omitted in production|digest property|server components render)\b/i,
  /\b\d{1,3}(\.\d{1,3}){3}(:\d+)?\b/, // ip address
  /<\/?[a-z][\w-]*(\s[^>]*)?>/i, // markup — an error PAGE, not a sentence
  /^\S{16,}$/, // a bare identifier, not a sentence
];

/** Segments that describe the payload's shape, not a thing the traveler
 *  filled in. Naming a field "Items" helps nobody.
 *
 *  These are WIRE PATH SEGMENTS, not prose — whatever the API happens to call
 *  a slot in its error envelope. BOTH spellings of traveller stay listed, and
 *  neither answers to our copy style: a storefront spelling sweep once rewrote
 *  the British one, which left this Set holding "travelers" twice (a silent
 *  no-op in a Set literal) and recognising no "travellers" segment at all. An
 *  endpoint emitting that segment would then have shown the traveller
 *  "Travellers" as though it were a field they had filled in. Changing a
 *  string here is a CONTRACT change; match what the API sends. */
const STRUCTURAL = new Set([
  "items",
  "body",
  "data",
  "customer",
  "travelers",
  "travellers",
  "checkoutfieldanswers",
  "answers",
]);

/** Our wire names, in the traveler's words. `pax` is the clearest case: it is
 *  what the API calls head-count and it is not a word anyone else uses. */
const FIELD_ALIASES: Record<string, string> = {
  pax: "Travelers",
  fullname: "Full name",
  firstname: "First name",
  lastname: "Last name",
  phone: "Phone number",
  email: "Email address",
  dob: "Date of birth",
  couponcode: "Promo code",
  departuredate: "Departure date",
};

/**
 * Which field a reason is about, from the `path` the API already sends.
 *
 * Without this, two genuinely different problems arrive as the same sentence
 * — `[["customer","email"],["items",0,"pax"]]` both read "This field is
 * required" — and de-duplication then collapses them into ONE line naming
 * nothing. The traveler is told a field is required without being told which,
 * and cannot see there were two.
 */
export function labelForPath(path: unknown[] | undefined): string | undefined {
  if (!Array.isArray(path)) return undefined;
  const segments = path.filter((p): p is string => typeof p === "string");
  for (let i = segments.length - 1; i >= 0; i -= 1) {
    const key = segments[i]!.toLowerCase();
    if (STRUCTURAL.has(key)) continue;
    // ONLY an aliased key gets a label. Title-casing whatever the wire happens
    // to call a field turned internal plumbing into what looks like a form the
    // traveler filled in: `quoteId` became "Quote id", `experienceId` became
    // "Experience id", and a custom question's own uuid became a nonsense
    // heading. This module's rule for messages — an id the traveler has never
    // seen and cannot change is worse than saying nothing, because they will
    // try to fix it — applies just as much to the label in front of one.
    // An unrecognised key means the reason stands on its own.
    return FIELD_ALIASES[key];
  }
  return undefined;
}

export function isMachinePhrasing(message: string): boolean {
  if (DISCLOSURE.some((pattern) => pattern.test(message))) return true;
  return MACHINE_PHRASING.test(message);
}

/**
 * What the BROWSER says when a request never left the device, mapped to what a
 * traveler can act on.
 *
 * These strings are written by the browser engine, not by us and not by the
 * API, so nothing else in this file catches them: "Failed to fetch" is plain
 * enough English to pass `isMachinePhrasing` and specific enough to pass
 * `isGeneric`. Found in live QA — a cart write with the connection dropped put
 * the words "Failed to fetch" in front of a customer, under a heading that
 * said "Nothing was added".
 *
 * One sentence of fact and one thing to check. No "try again in a moment": we
 * do not know when their connection comes back.
 */
const TRANSPORT_FAILURE =
  /^(failed to fetch|load failed|network ?error|networkerror when attempting to fetch resource\.?|the network connection was lost\.?|the internet connection appears to be offline\.?|connection (failed|lost)|fetch failed)$/i;

export function humanForTransportError(message: string): string | undefined {
  return TRANSPORT_FAILURE.test(message.trim())
    ? "Your device could not reach us. Check your internet connection."
    : undefined;
}

/**
 * A stale deployment — the page is older than the server answering it.
 *
 * Next mints a Server Action id at BUILD time. A browser still holding
 * JavaScript from an earlier deployment sends an id the current one has never
 * heard of, and Next throws
 * `Server Action "40d0ce…" was not found on the server. Read more:
 * https://nextjs.org/docs/messages/failed-to-find-server-action`.
 *
 * That sentence is ordinary English, so it clears every filter in
 * `describeThrown` and went to the customer verbatim — a hash and a
 * nextjs.org link inside "We couldn't add this to your cart", seen on staging
 * while adding a package.
 *
 * Reloading genuinely resolves it: the browser fetches the current build. That
 * is a statement of fact, not a promise that retrying the same click helps —
 * it will not, because the id is gone for good.
 *
 * The PREVENTION is deployment-side, not here: Vercel's Skew Protection pins a
 * client to the deployment that served it. This repo sets no `vercel.json`, so
 * it is off, and every deploy can strand anyone mid-session.
 */
const STALE_DEPLOYMENT = /server action\b.*\bwas not found on the server/i;

export function humanForStaleDeployment(message: string): string | undefined {
  return STALE_DEPLOYMENT.test(message.trim())
    ? "This page was loaded before the site was updated. Reload the page, then add it again."
    : undefined;
}
